import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTranslation } from '../../utils/translationHelpers';
import { FUNNEL_GATES_ENABLED } from '../../config/funnelConfig';
import { aiExamFunnelTracker } from '../../services/aiExamFunnelTracker';
import { funnelEventTracker } from '../../services/funnelEventTracker';
import {
  identifyWeakAreas,
  type TopicResult,
  type WeakArea,
} from '../../utils/practiceLogic';
import type { Question } from '../../types/passFirstExam';
import type { Language } from '../../types_i18n';
import { saveWeakAreas } from '../../services/weakAreaQuestionService';
import { getAllTopicPerformance } from '../../services/topicPerformanceService';
import { buildAIExamQuestions, buildTopicScoresFromPerformance } from './AIExam.utils';
import { getTranslatedTestName } from '../../services/aiCoach.metadata';
import { TEST_QUESTION_MAP } from '../PracticeTest/PracticeTest.constants';
import { translateQuestionsById } from '../../services/questionTranslationService';
import {
  getExamUnlockState,
  completeExamInstance,
  recordRetakeAttempt,
  getLastCompletedInstanceId,
  getCachedQuestionsForInstance,
  setCachedQuestionsForInstance,
  isInstanceCompleted,
} from '../../services/aiExamUnlockService';
import { testCountTracker } from '../../services/testCountTracker';
import { isUserSubscribed, isAdminOverride, FREE_TOPICS } from '../../services/subscriptionGate';
import { HardGate } from '../HardGate/HardGate';
import { getUserProgress } from '../../services/userProgressService';
import { saveLastExamScore, getLastExamScore } from '../../services/supabaseService';
import { QuestionAnimationPlayer, preloadAnimationVideo, preloadAnimationsForQuestions } from '../PracticeTest/QuestionAnimationPlayer';
import { assetPreloader } from '../../services/assetPreloader';
import { usePrefetchNextQuestions } from '../../hooks/usePrefetchNextQuestions';
import { useNetworkAwareness } from '../../services/networkAwareLoading';
import { offlineSync, useOfflineSync } from '../../services/offlineSync';
import { ImageWithLQIP } from '../ImageWithLQIP/ImageWithLQIP';
import { aiCoach } from '../../services/aiCoach';
import { AIExamIntroPage } from './AIExamIntroPage';
import '../MockExam.css';
import '../RealCBRExam.css';

const LANG_MAP: { [key in Language]: 'NL' | 'EN' | 'AR' } = {
  nl: 'NL',
  en: 'EN',
  ar: 'AR',
};

const _EXAM_SIZE = 50;
const TIME_LIMIT = 30 * 60;
const PASS_THRESHOLD = 44;

const EXAM_FULL_VIEWPORT_STYLE: React.CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: '100vw',
  height: '100dvh',
  zIndex: 9000,
  background: '#ffffff',
  overflowY: 'auto',
  margin: 0,
  padding: 0,
};

export default function AIExam() {
  const navigate = useNavigate();
  const { currentLanguage, t, t_nested } = useLanguage();
  const lang = LANG_MAP[currentLanguage];

  // RTL (Arabic): prevent numeric fractions like "2/50" from visually flipping to "50/2".
  const isolateFractions = (input: string): string =>
    input.replace(/(\d+\s*\/\s*\d+)/g, '\u2066$1\u2069');

  const [topicScores, setTopicScores] = useState<import('./AIExam.utils').TopicScore[]>([]);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (userId) {
      getAllTopicPerformance(userId)
        .then((perf) => setTopicScores(buildTopicScoresFromPerformance(perf)))
        .catch(() => setTopicScores(buildTopicScoresFromPerformance([])));
    } else {
      setTopicScores(buildTopicScoresFromPerformance([]));
    }
  }, []);

  const isFreeExam = !isUserSubscribed() && !testCountTracker.getFreeAiExamUsed();
  const questionsGenerated = useMemo(() => {
    const scores = topicScores.length > 0 ? topicScores : buildTopicScoresFromPerformance([]);
    return buildAIExamQuestions(scores, TEST_QUESTION_MAP, lang, isFreeExam);
  }, [topicScores, lang, isFreeExam]);

  // Retake: load cached questions for last completed instance
  const unlockState = getExamUnlockState();
  const examReady = unlockState.activeExamInstanceId !== null;
  const lastCompletedId = getLastCompletedInstanceId();
  const cachedForRetake = !examReady && lastCompletedId
    ? (getCachedQuestionsForInstance(lastCompletedId) as Question[] | null)
    : null;
  const questions = (cachedForRetake && cachedForRetake.length > 0)
    ? cachedForRetake
    : questionsGenerated;

  // Translate question text and options into the user's chosen language.
  // `questions` (English) is kept for answer ID matching and caching.
  const translatedQuestions = useMemo(
    () => translateQuestionsById(questions, currentLanguage as 'en' | 'nl' | 'ar'),
    [questions, currentLanguage]
  );

  const [searchParams] = useSearchParams();
  const skipIntro = searchParams.get('skipIntro') === 'true';
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<'intro' | 'exam' | 'results'>(skipIntro ? 'exam' : 'intro');
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT);
  const [flaggedQuestions, setFlaggedQuestions] = useState<Set<number>>(new Set());
  const [focusBreakCount, setFocusBreakCount] = useState(0);
  const [showFocusBreakBanner, setShowFocusBreakBanner] = useState(false);
  const [showMistakes, setShowMistakes] = useState(false);
  const [lastExamScore, setLastExamScore] = useState<number | null>(null);
  const [aiExamAttemptScores, setAiExamAttemptScores] = useState<number[]>([]);
  const [isResultsRetake, setIsResultsRetake] = useState(false);
  const [showHardGate, setShowHardGate] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const funnelTrackedRef = useRef(false);
  const resultsScrollRef = useRef<HTMLDivElement>(null);
  const isTimeUpRef = useRef(false);
  const currentExamInstanceIdRef = useRef<string | null>(null);
  const skipIntroInitializedRef = useRef(false);

  /* Hide mobile footer during exam/results only — keep FooterNav visible on intro (bio) page */
  useEffect(() => {
    if (phase === 'intro') return undefined;
    const isMobile = window.innerWidth <= 768;
    if (!isMobile) return undefined;
    const footer = document.querySelector('footer') as HTMLElement;
    const footerNav = document.querySelector('.footer-nav') as HTMLElement;
    const bottomNav = document.querySelector('.bottom-nav') as HTMLElement;
    const tabBar = document.querySelector('.tab-bar') as HTMLElement;
    [footer, footerNav, bottomNav, tabBar].forEach((el) => {
      if (el) el.style.setProperty('display', 'none', 'important');
    });
    return () => {
      [footer, footerNav, bottomNav, tabBar].forEach((el) => {
        if (el) el.style.removeProperty('display');
      });
    };
  }, [phase]);

  // skipIntro: initialize exam when coming from TestsPage with ?skipIntro=true
  useEffect(() => {
    if (!skipIntro || questions.length === 0 || phase !== 'exam' || skipIntroInitializedRef.current) return;
    skipIntroInitializedRef.current = true;
    if (examReady && unlockState.activeExamInstanceId) {
      currentExamInstanceIdRef.current = unlockState.activeExamInstanceId;
      setCachedQuestionsForInstance(unlockState.activeExamInstanceId, questions);
    } else if (lastCompletedId) {
      currentExamInstanceIdRef.current = lastCompletedId;
    }
    setIndex(0);
    setAnswers({});
    setTimeLeft(TIME_LIMIT);
  }, [skipIntro, questions.length, phase, examReady, unlockState.activeExamInstanceId, lastCompletedId, questions]);

  // Notify footer to show on mobile when intro phase only
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ai-exam-phase-change', { detail: { phase } }));
    return () => {
      window.dispatchEvent(new CustomEvent('ai-exam-phase-change', { detail: { phase: null } }));
    };
  }, [phase]);

  // Results screen: scroll-to-top FAB visibility (scroll container = full-viewport wrapper)
  useEffect(() => {
    if (phase !== 'results') {
      setShowScrollTop(false);
      return;
    }
    const el = resultsScrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      setShowScrollTop(el.scrollTop > 200);
    };
    handleScroll();
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [phase]);

  // Load last exam score and all attempt scores for intro page
  useEffect(() => {
    const loadScore = async () => {
      // Load all attempt scores from local history
      const history = aiCoach.getTestHistory().filter((t: { testId: string }) => t.testId === 'ai-exam');
      const scores = history
        .map((t: { score?: number; percentage?: number }) =>
          t.score != null ? t.score : t.percentage != null ? Math.round((t.percentage / 100) * 50) : null
        )
        .filter((s: number | null): s is number => s != null);
      setAiExamAttemptScores(scores);

      const userId = localStorage.getItem('userId');
      if (userId) {
        const raw = await getLastExamScore(userId);
        if (raw != null) {
          setLastExamScore(raw);
          return;
        }
        const p = await getUserProgress(userId).catch(() => null);
        if (p?.lastExamScore != null) {
          setLastExamScore(Math.round((p.lastExamScore / 100) * 50));
          return;
        }
      }
      const mostRecent = history.length > 0 ? history[history.length - 1] : null;
      if (mostRecent?.score != null) setLastExamScore(mostRecent.score);
      else if (mostRecent?.percentage != null) setLastExamScore(Math.round((mostRecent.percentage / 100) * 50));
    };
    loadScore();
  }, []);

  const total = questions.length;
  const currentQuestion: Question | undefined = translatedQuestions[index];
  const selectedAnswer = answers[index];

  // Batch-preload all animation videos when questions first load
  // Phase 4a: Also preload all exam assets for instant access
  useEffect(() => {
    if (translatedQuestions.length > 0) {
      preloadAnimationsForQuestions(translatedQuestions);
      // Phase 4a: Preload all 50 exam questions for instant navigation
      assetPreloader.preloadExamAssets(translatedQuestions);
    }
  }, [translatedQuestions.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Preload the next question's animation video as a secondary signal
  const nextQuestion: Question | undefined = translatedQuestions[index + 1];
  useEffect(() => {
    preloadAnimationVideo(nextQuestion?.animationId);
  }, [nextQuestion?.animationId]);

  // Phase 4a: Smart prefetch next 2-5 questions during exam
  usePrefetchNextQuestions({
    currentIndex: index,
    totalQuestions: translatedQuestions.length,
    questions: translatedQuestions,
    enabled: true
  });

  // Phase 4c: Network awareness for quality adjustment
  const { status: _networkStatus, qualityTier: _qualityTier } = useNetworkAwareness();

  // Phase 4d: Offline sync - queue answers during offline, sync when online
  const { isSyncing: _isSyncing, pendingExamsCount: _pendingExamsCount } = useOfflineSync();

  const answersRef = useRef<Record<number, string>>({});
  answersRef.current = answers;

  const saveExamOnTimerExpiry = useCallback(async () => {
    const ans = answersRef.current;
    const tot = questions.length;
    const correctCount = Object.entries(ans).filter(
      ([i, id]) => questions[Number(i)]?.answerId === id
    ).length;
    const score = Math.round((correctCount / tot) * 100);
    const passed = correctCount >= PASS_THRESHOLD;
    const date = new Date().toISOString();

    const byTopic: Record<string, { correct: number; total: number }> = {};
    questions.forEach((q, i) => {
      const t = q.topic || q.id;
      if (!byTopic[t]) byTopic[t] = { correct: 0, total: 0 };
      byTopic[t].total += 1;
      if (q.answerId === ans[i]) byTopic[t].correct += 1;
    });
    const topicResults: TopicResult[] = Object.entries(byTopic).map(
      ([topic, { correct, total: totCount }]) => ({ topic, correct, total: totCount })
    );
    const weakAreas: WeakArea[] = identifyWeakAreas(topicResults);
    const weakScores: Record<string, number> = {};
    weakAreas.forEach((w) => { weakScores[w.topic] = w.score; });
    saveWeakAreas({ topics: weakAreas.map(w => w.topic), scores: weakScores });

    const instanceId = currentExamInstanceIdRef.current;
    if (instanceId) {
      const isRetake = isInstanceCompleted(instanceId);
      if (isRetake) {
        recordRetakeAttempt(instanceId, correctCount, tot, passed, date);
      } else {
        completeExamInstance(instanceId, correctCount, tot, passed, date);
      }
    }

    try {
      const { aiCoach } = await import('../../services/aiCoach');
      await aiCoach.saveTestResult({
        testId: 'ai-exam',
        testName: t('aiExamPage.title'),
        score: correctCount,
        totalQuestions: tot,
        percentage: score,
        date,
      });
    } catch (e) {
      console.warn('Could not sync exam to aiCoach:', e);
    }
    // Bust recommendation cache so dashboard shows a practice test, not the just-completed AI exam
    try {
      const { memoizedRecommendations } = await import('../../services/memoizedRecommendations');
      memoizedRecommendations.invalidateAll();
    } catch (e) {
      console.warn('Could not invalidate recommendation cache:', e);
    }
    try {
      const userId = localStorage.getItem('userId');
      if (userId) {
        const { saveExamResults } = await import('../../services/userProgressService');
        await saveExamResults(userId, score, weakAreas);
        const ok = await saveLastExamScore(userId, correctCount);
        if (!ok) console.error('Could not save last exam score to Supabase');
      }
    } catch (e) {
      console.error('Error saving exam results:', e);
    }
  }, [questions, t]);

  // Exam realism: focus/visibility guardrails (timer keeps running)
  useEffect(() => {
    if (phase !== 'exam') return;

    const bumpFocusBreak = () => {
      setFocusBreakCount((c) => c + 1);
      setShowFocusBreakBanner(true);
      window.setTimeout(() => setShowFocusBreakBanner(false), 3500);
    };

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') bumpFocusBreak();
    };
    const onBlur = () => bumpFocusBreak();

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== 'exam') return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          isTimeUpRef.current = true;
          saveExamOnTimerExpiry();
          setPhase('results');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [phase, saveExamOnTimerExpiry]);

  // Funnel: track AI exam completion when entering results (once per exam)
  const correctCountRef = useRef(0);
  const totalRef = useRef(0);
  const passedRef = useRef(false);
  if (phase === 'results') {
    correctCountRef.current = Object.entries(answers).filter(
      ([i, id]) => questions[Number(i)]?.answerId === id
    ).length;
    totalRef.current = questions.length;
    passedRef.current = correctCountRef.current >= PASS_THRESHOLD;
  }
  useEffect(() => {
    if (phase !== 'results' || !FUNNEL_GATES_ENABLED || funnelTrackedRef.current) return;
    funnelTrackedRef.current = true;
    const score = totalRef.current > 0 ? Math.round((correctCountRef.current / totalRef.current) * 100) : 0;
    const countBefore = aiExamFunnelTracker.getCount();
    aiExamFunnelTracker.increment();
    if (countBefore === 0) funnelEventTracker.aiExam1Completed(score, passedRef.current);
    else if (countBefore === 1) funnelEventTracker.aiExam2Completed(score, passedRef.current);
  }, [phase]);

  // After free AI exam results: mark free exam used (Retake / Dashboard gating for non-subscribers)
  useEffect(() => {
    if (phase === 'results' && !isUserSubscribed()) {
      testCountTracker.setFreeAiExamUsed();
    }
  }, [phase]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startExam = () => {
    if (questions.length === 0) return;
    if (!isAdminOverride() && !isUserSubscribed() && testCountTracker.getFreeAiExamUsed()) {
      setShowHardGate(true);
      return;
    }
    if (examReady && unlockState.activeExamInstanceId) {
      currentExamInstanceIdRef.current = unlockState.activeExamInstanceId;
      setCachedQuestionsForInstance(unlockState.activeExamInstanceId, questions);
    } else if (lastCompletedId) {
      currentExamInstanceIdRef.current = lastCompletedId;
    }
    setPhase('exam');
    setIndex(0);
    setAnswers({});
    setTimeLeft(TIME_LIMIT);
  };

  const handleAnswer = (optionId: string) => {
    if (phase !== 'exam') return;
    setAnswers(prev => ({ ...prev, [index]: optionId }));

    // Phase 4d: Offline sync - queue answer for offline/sync scenarios
    const currentQuestion = translatedQuestions[index];
    if (currentQuestion) {
      const currentExamId = currentExamInstanceIdRef.current || `exam-${Date.now()}`;
      offlineSync.queueAnswer(currentExamId, {
        questionId: currentQuestion.id,
        selectedOption: optionId,
        timeTaken: TIME_LIMIT - timeLeft,
        timestamp: Date.now()
      });
    }
  };

  const goToNext = () => {
    if (index < total - 1) setIndex(i => i + 1);
  };

  const goToPrevious = () => {
    if (index > 0) setIndex(i => i - 1);
  };

  const toggleFlag = (idx: number) => {
    setFlaggedQuestions(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const finishExam = useCallback(() => {
    isTimeUpRef.current = false;
    saveExamOnTimerExpiry();
    setPhase('results');
  }, [saveExamOnTimerExpiry]);

  // --- INTRO SCREEN ---
  if (phase === 'intro') {
    const retakeReady = !examReady && !!lastCompletedId && questions.length > 0;
    const freeFirstTimeReady = isFreeExam && questions.length > 0;
    const ctaReady = examReady || retakeReady || freeFirstTimeReady;
    const ctaDisabled = !ctaReady || questions.length === 0;
    const isUnlocked = examReady || retakeReady || isFreeExam;

    const translate = (key: string) => t_nested(key) || key;
    const formatTopicName = (id: string) => getTranslatedTestName(id, translate);

    const realWeakTopics = topicScores
      .filter((t) => t.hasRealData && t.accuracy < 70)
      .sort((a, b) => a.accuracy - b.accuracy)
      .slice(0, 3);
    const hasAnyRealData = topicScores.some((t) => t.hasRealData);
    const fallbackTopics = hasAnyRealData
      ? [...topicScores].sort((a, b) => a.accuracy - b.accuracy).slice(0, 3)
      : [...topicScores].sort(() => Math.random() - 0.5).slice(0, 3);
    const showBreakdownBlank = !topicScores.some((t) => t.hasRealData);
    const weakTopicList = showBreakdownBlank
      ? (FREE_TOPICS as string[]).slice(0, 3).map((id) => ({ id, name: formatTopicName(id), accuracy: 0 }))
      : (realWeakTopics.length > 0 ? realWeakTopics : fallbackTopics)
          .map((t) => ({ id: t.topicId, name: formatTopicName(t.topicId), accuracy: t.accuracy }));

    const n = aiExamAttemptScores.length;
    const bestScoreOut50 = n > 0 ? Math.max(...aiExamAttemptScores) : null;
    const worstScoreOut50 = n > 0 ? Math.min(...aiExamAttemptScores) : null;
    const avgScoreOut50 = n > 0 ? Math.round(aiExamAttemptScores.reduce((a, b) => a + b, 0) / n) : null;

    return (
      <div className="ai-exam-intro-wrapper">
        {showHardGate && <HardGate />}
        <AIExamIntroPage
          isUnlocked={isUnlocked}
          completedPracticeCount={unlockState.completedPracticeCount}
          nextUnlockThreshold={Math.max(unlockState.nextUnlockThreshold, 1)}
          lastScoreOut50={lastExamScore}
          attemptCount={n}
          bestScoreOut50={bestScoreOut50}
          worstScoreOut50={worstScoreOut50}
          avgScoreOut50={avgScoreOut50}
          weakTopics={weakTopicList}
          showBreakdownBlank={showBreakdownBlank}
          onStartExam={startExam}
          ctaDisabled={ctaDisabled}
        />
      </div>
    );
  }

  // --- RESULTS SCREEN ---
  if (phase === 'results') {
    const correctCount = Object.entries(answers).filter(
      ([i, id]) => questions[Number(i)]?.answerId === id
    ).length;
    const percent = total > 0 ? Math.round((correctCount / total) * 100) : 0;
    const isPassed = correctCount >= PASS_THRESHOLD;
    const isTimeUp = isTimeUpRef.current;
    const isSubscribed = isUserSubscribed();

    const wrongAnswers = translatedQuestions.map((q, i) => ({
      question: q,
      index: i,
      userAnswer: answers[i],
      correctAnswer: questions[i]?.answerId ?? q.answerId,
      isWrong: answers[i] !== (questions[i]?.answerId ?? q.answerId),
    })).filter(x => x.isWrong);

    const getOptionText = (q: Question, optionId: string) => {
      const opt = (q.options || []).find(o => o.id === optionId);
      return opt?.text ?? '';
    };

    const handleRetake = () => {
      if (!isSubscribed) {
        setShowHardGate(true);
        return;
      }
      setIsResultsRetake(true);
      setIndex(0);
      setAnswers({});
      setTimeLeft(TIME_LIMIT);
      setPhase('exam');
      setFlaggedQuestions(new Set());
    };

    const handleDashboard = () => {
      if (!isSubscribed) {
        setShowHardGate(true);
        return;
      }
      navigate('/dashboard', { replace: true });
    };

    const passLabel = getTranslation(t_nested, 'aiExam.passLabel', 'Passed! 🎉');
    const failLabel = getTranslation(t_nested, 'aiExam.failLabel', 'Not passed');
    const passReminder = isolateFractions(
      getTranslation(t_nested, 'aiExam.passReminder', 'Minimum 44/50 required')
    );
    const timesUpLabel = getTranslation(t_nested, 'aiExam.timesUp', 'Time is up');
    const wrongAnswersTitle = wrongAnswers.length > 0
      ? getTranslation(t_nested, 'aiExam.wrongAnswers', 'Your mistakes ([n])').replace('[n]', String(wrongAnswers.length))
      : getTranslation(t_nested, 'aiExam.perfectScore', 'Perfect! No mistakes');
    const yourAnswerLabel = getTranslation(t_nested, 'aiExam.yourAnswer', 'Your answer');
    const correctAnswerLabel = getTranslation(t_nested, 'aiExam.correctAnswer', 'Correct answer');
    const notAnsweredLabel = getTranslation(t_nested, 'aiExam.notAnswered', 'Not answered');

    const handleScrollTop = () => {
      if (resultsScrollRef.current) {
        resultsScrollRef.current.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    };

    return ReactDOM.createPortal(
      <div
        ref={resultsScrollRef}
        style={EXAM_FULL_VIEWPORT_STYLE}
        data-ai-exam-results
      >
        {showHardGate && <HardGate />}
        <div className="ai-exam-results-page">
        <div className="ai-exam-results-card">
          {isTimeUp && (
            <div className="ai-exam-results-times-up-banner">{timesUpLabel}</div>
          )}
          <h2 className="ai-exam-results-status" style={{ color: isPassed ? '#16a34a' : '#dc2626' }}>
            {isPassed ? passLabel : failLabel}
          </h2>
          <div className="ai-exam-results-score">{correctCount}</div>
          <div className="ai-exam-results-fraction">
            <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>
              {correctCount} / 50
            </span>
          </div>
          <div className="ai-exam-results-percent">{percent}%</div>
          <p className="ai-exam-results-reminder">{passReminder}</p>
          <div className="ai-exam-results-buttons">
            {!isResultsRetake && (
              <button type="button" className="ai-exam-results-retake" onClick={handleRetake}>
                {getTranslation(t_nested, 'aiExam.results.retake', 'Retake')}
              </button>
            )}
            <button type="button" className="ai-exam-results-dashboard" onClick={handleDashboard}>
              {getTranslation(t_nested, 'aiExam.results.dashboard', 'Dashboard')}
            </button>
          </div>
        </div>

        <div
          className={`ai-exam-mistakes-header ${wrongAnswers.length === 0 ? 'ai-exam-mistakes-perfect' : ''}`}
          onClick={wrongAnswers.length > 0 ? () => setShowMistakes((prev) => !prev) : undefined}
          style={{ cursor: wrongAnswers.length === 0 ? 'default' : 'pointer' }}
        >
          <span className="ai-exam-mistakes-header-text">{wrongAnswersTitle}</span>
          {wrongAnswers.length > 0 && (
            <span className="ai-exam-mistakes-chevron">{showMistakes ? '▲' : '▼'}</span>
          )}
        </div>
        <div className={`ai-exam-mistakes-expanded ${showMistakes ? 'expanded' : ''}`}>
          {wrongAnswers.map(({ question: q, index: i, userAnswer }) => (
            <div key={i} className="ai-exam-wrong-card">
              <div className="ai-exam-wrong-question-label">
                {getTranslation(t_nested, 'aiExam.questionLabel', 'QUESTION [n]').replace('[n]', String(i + 1))}
              </div>
              <p className="ai-exam-wrong-question-text">{q.question || q.text}</p>
              <QuestionAnimationPlayer animationId={q.animationId} />
              {!q.animationId && q.imageUrl && (
                <img
                src={q.imageUrl}
                alt=""
                className="ai-exam-wrong-image"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const parent = target.parentElement;
                  if (parent) parent.style.display = 'none';
                }}
              />
              )}
              <div className="ai-exam-wrong-your-answer">
                <div className="ai-exam-wrong-label">{yourAnswerLabel}</div>
                <div className="ai-exam-wrong-value">
                  {userAnswer ? getOptionText(q, userAnswer) : <em>{notAnsweredLabel}</em>}
                </div>
              </div>
              <div className="ai-exam-wrong-correct-answer">
                <div className="ai-exam-wrong-label">{correctAnswerLabel}</div>
                <div className="ai-exam-wrong-value">{getOptionText(q, q.answerId)}</div>
              </div>
            </div>
          ))}
        </div>
        <button
          type="button"
          className={`scroll-to-top-fab${showScrollTop ? '' : ' hidden'}`}
          onClick={handleScrollTop}
          aria-label={t('aria.scrollToTop')}
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>
        </div>
      </div>,
      document.body
    );
  }

  // --- EXAM SCREEN ---
  if (!currentQuestion) return null;

  const progressPercent = (timeLeft / TIME_LIMIT) * 100;
  const focusBreakText = getTranslation(
    t_nested,
    'aiExam.focusBreakBanner',
    'You left the exam — timer keeps running.'
  );
  const focusBreakCountText = getTranslation(
    t_nested,
    'aiExam.focusBreakCount',
    'Focus breaks: [n]'
  ).replace('[n]', String(focusBreakCount));

  // Portal to document.body so position:fixed escapes any ancestor transform
  // (main-content-inner has transform:matrix which creates a containing block,
  //  shifting the fixed root 76px from the viewport top and cutting off the bottom nav)
  return ReactDOM.createPortal(
    <div className="ai-exam-full-viewport-root" style={EXAM_FULL_VIEWPORT_STYLE}>
      {showHardGate && <HardGate />}
      <div className="ai-exam-container">
      {/* Exam realism: visibility / focus break banner */}
      {showFocusBreakBanner && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            padding: '10px 14px',
            background: '#0F172A',
            color: '#fff',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            userSelect: 'none',
          }}
        >
          <span>{focusBreakText}</span>
          <span style={{ opacity: 0.9 }}>{focusBreakCountText}</span>
        </div>
      )}
      <header className="ai-exam-header">
        <span className="ai-exam-header-counter">
          {isolateFractions(
            getTranslation(t_nested, 'aiExam.headerCounter', 'Question [n] / 50').replace('[n]', String(index + 1))
          )}
        </span>
        <span className={`ai-exam-header-timer ${timeLeft <= 120 ? 'critical' : timeLeft <= 300 ? 'warning' : ''}`}>
          {formatTime(timeLeft)}
        </span>
        <div className="ai-exam-header-actions">
          <button
            type="button"
            className={`ai-exam-header-btn ${flaggedQuestions.has(index) ? 'active' : ''}`}
            onClick={() => toggleFlag(index)}
            aria-label={t('aria.flag')}
          >
            {getTranslation(t_nested, 'aiExam.flagMark', 'F')}
          </button>
          <button
            type="button"
            className="ai-exam-header-btn"
            onClick={() => {
              if (window.confirm(getTranslation(t_nested, 'quiz.exitConfirm', 'Are you sure you want to exit the exam?'))) {
                navigate('/dashboard');
              }
            }}
            aria-label={t('aria.exit')}
          >
            &#215;
          </button>
        </div>
      </header>

      <div className={`ai-exam-progress-bar ${timeLeft <= 120 ? 'critical' : timeLeft <= 300 ? 'warning' : ''}`}>
        <div className="ai-exam-progress-fill" style={{ width: `${progressPercent}%` }} />
      </div>

      <div className="ai-exam-content-wrapper">
      <div className="ai-exam-question-area">
        <div className="ai-exam-question-label">
          {getTranslation(t_nested, 'aiExam.questionShort', 'QUESTION [n] OF 50').replace('[n]', String(index + 1))}
        </div>
        <h2 className="ai-exam-question-text">{currentQuestion.question || currentQuestion.text}</h2>
        <QuestionAnimationPlayer animationId={currentQuestion.animationId} />
        {!currentQuestion.animationId && currentQuestion.imageUrl && (
          /* Phase 4b: Progressive image loading with LQIP (blur-up effect) */
          <ImageWithLQIP
            src={currentQuestion.imageUrl}
            alt={currentQuestion.imageHint || getTranslation(t_nested, 'aiExam.questionImageAlt', 'Question illustration')}
            className="ai-exam-question-image"
            objectFit="contain"
          />
        )}
        <div className="ai-exam-options-group">
        <div className="ai-exam-answer-options">
          {(currentQuestion.options || []).map((option) => {
            const isSelected = selectedAnswer === option.id;
            return (
              <div
                key={option.id}
                className={`ai-exam-answer-option ${isSelected ? 'selected' : ''}`}
                onClick={() => handleAnswer(option.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleAnswer(option.id);
                  }
                }}
              >
                <div className={`ai-exam-radio ${isSelected ? 'selected' : ''}`} />
                <span className="ai-exam-option-text">{option.text}</span>
              </div>
            );
          })}
        </div>
        </div>
      </div>

      <div className="ai-exam-bottom-nav ai-exam-nav-button-row">
        <button
          type="button"
          className="ai-exam-nav-prev"
          onClick={goToPrevious}
          disabled={index === 0}
        >
          {getTranslation(t_nested, 'quiz.previous', 'Previous')}
        </button>
        {index < total - 1 ? (
          <button type="button" className="ai-exam-nav-next" onClick={goToNext}>
            {getTranslation(t_nested, 'quiz.next', 'Next')}
          </button>
        ) : (
          <button type="button" className="ai-exam-nav-next" onClick={() => finishExam()}>
            {getTranslation(t_nested, 'aiExam.finishLabel', 'Finish')} →
          </button>
        )}
      </div>
      </div>
      </div>
    </div>,
    document.body
  );
}
